<script setup>
import { computed, onMounted, ref } from "vue";
import { ElMessageBox } from "element-plus";
import HeaderTitle from "@/components/layout/HeaderTitle";
import EmployeePermissionMatrix from "@/components/security/EmployeePermissionMatrix.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import Notification from "@/utils/plugins/NotificationService";
import PermissionProfileService from "@/api/services/permissionProfile.service";
import EmployeePermissionService from "@/api/services/employeePermission.service";

const page = ref(1);
const itemsPerPage = ref(10);
const profiles = ref([]);
const catalog = ref([]);
const showProfileDialog = ref(false);
const saving = ref(false);
const editingProfileId = ref(null);
const profileForm = ref({ name: "", appliesToEmployeeType: "" });
const permissions = ref({});
const initialPermissions = ref({});

const employeeTypeOptions = [
  { value: "employee", label: "Empleado" },
  { value: "intern", label: "Becario" },
  { value: "external_collaborator", label: "Colaborador externo" },
];
const employeeTypeLabel = (value) => employeeTypeOptions.find((o) => o.value === value)?.label ?? "Todos";

const listedProfiles = computed(() => profiles.value.slice((page.value - 1) * itemsPerPage.value, page.value * itemsPerPage.value));

const loadProfiles = () => {
  PermissionProfileService.getProfiles().then((res) => {
    if (!res) return;
    profiles.value = res.data ?? [];
  });
};

const loadCatalog = () => {
  EmployeePermissionService.getCatalogActions().then((res) => {
    if (!res) return;
    catalog.value = res.data ?? [];
  });
};

const applyGrants = (grants) => {
  const map = {};
  (grants ?? []).forEach((grant) => { map[`${grant.module}:${grant.action}`] = true; });
  permissions.value = map;
  initialPermissions.value = { ...map };
};

const openNewProfile = () => {
  editingProfileId.value = null;
  profileForm.value = { name: "", appliesToEmployeeType: "" };
  applyGrants([]);
  showProfileDialog.value = true;
};

const openProfile = (profile) => {
  editingProfileId.value = profile.id;
  profileForm.value = { name: profile.name, appliesToEmployeeType: profile.appliesToEmployeeType ?? "" };
  applyGrants(profile.grants);
  showProfileDialog.value = true;
};

const toggle = (module, action) => {
  const key = `${module}:${action}`;
  permissions.value = { ...permissions.value, [key]: !permissions.value[key] };
};

const saveProfile = () => {
  if (!profileForm.value.name.trim()) {
    Notification.warning("El nombre del perfil es obligatorio.", 3500);
    return;
  }

  const isNew = editingProfileId.value === null;
  const grantedRows = Object.keys(permissions.value)
    .filter((key) => permissions.value[key] && (isNew || permissions.value[key] !== initialPermissions.value[key]))
    .map((key) => { const [module, action] = key.split(":"); return { module, action, granted: true }; });
  const revokedRows = isNew ? [] : Object.keys(initialPermissions.value)
    .filter((key) => initialPermissions.value[key] && !permissions.value[key])
    .map((key) => { const [module, action] = key.split(":"); return { module, action, granted: false }; });

  const body = {
    name: profileForm.value.name.trim(),
    appliesToEmployeeType: profileForm.value.appliesToEmployeeType || null,
    grants: [...grantedRows, ...revokedRows],
  };

  saving.value = true;
  const request = isNew
    ? PermissionProfileService.createProfile(body)
    : PermissionProfileService.updateProfile(editingProfileId.value, body);

  request.then((res) => {
    if (!res) return;
    Notification.success(isNew ? "Plantilla creada correctamente." : "Plantilla actualizada correctamente.", 3000);
    showProfileDialog.value = false;
    loadProfiles();
  }).finally(() => { saving.value = false; });
};

const deleteProfile = (profile) => {
  ElMessageBox.confirm(
    `Se eliminará la plantilla "${profile.name}" de forma permanente. ¿Continuar?`,
    "Eliminar plantilla",
    { confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", type: "warning" }
  ).then(() => {
    PermissionProfileService.deleteProfile(profile.id).then((res) => {
      if (!res) return;
      Notification.success("Plantilla eliminada correctamente.", 3000);
      loadProfiles();
    });
  }).catch(() => { });
};

onMounted(() => {
  loadProfiles();
  loadCatalog();
});
</script>

<template>
  <section class="permission-profiles">
    <HeaderTitle title="Seguridad" />
    <div class="security-subtitle"><svg class="security-subtitle__icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
        <path d="M8.8 11.7 11 13.9l4.5-4.5" />
      </svg>
      <div class="title-text">Perfiles de permisos</div>
      <button class="btn btn_red permission-profiles__new" @click="openNewProfile">NUEVA PLANTILLA</button>
    </div>
    <div class="table table-not-mobile profiles-table">
      <table>
        <thead>
          <tr>
            <th>Nombre del perfil</th>
            <th>Tipo empleado</th>
            <th>Permisos concedidos</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="profile in listedProfiles" :key="profile.id" class="pointer" @click="openProfile(profile)">
            <td>{{ profile.name }}</td>
            <td>{{ employeeTypeLabel(profile.appliesToEmployeeType) }}</td>
            <td>{{ profile.grants.length }}</td>
            <td>
              <div class="row-actions"><font-awesome-layers class="fa-2x" title="Editar perfil"
                  @click.stop="openProfile(profile)"><font-awesome-icon icon="circle"
                    style="color:#f7d20e" /><font-awesome-icon icon="pen" class="fa-inverse"
                    transform="shrink-7" /></font-awesome-layers><font-awesome-layers class="fa-2x"
                  title="Eliminar perfil" @click.stop="deleteProfile(profile)"><font-awesome-icon icon="circle"
                    style="color:#f9151c" /><font-awesome-icon icon="times" class="fa-inverse"
                    transform="shrink-6" /></font-awesome-layers></div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!profiles.length" class="empty">No hay plantillas creadas todavía.</p>
    </div>
    <div class="table-mobile profiles-mobile"><el-card v-for="profile in listedProfiles" :key="profile.id"
        class="profile-card" @click="openProfile(profile)"><button class="profile-card__delete"
          @click.stop="deleteProfile(profile)"><font-awesome-layers><font-awesome-icon
              icon="circle" /><font-awesome-icon icon="times" class="fa-inverse"
              transform="shrink-6" /></font-awesome-layers></button>
        <p><b>Nombre del perfil:</b>{{ profile.name }}</p>
        <p><b>Tipo empleado:</b>{{ employeeTypeLabel(profile.appliesToEmployeeType) }}</p>
        <p><b>Permisos concedidos:</b>{{ profile.grants.length }}</p>
      </el-card>
      <p v-if="!profiles.length" class="empty">No hay plantillas creadas todavía.</p>
    </div>
    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="profiles.length"
      :page-size-options="[10, 20, 50]" class="pagination" @size-change="page = 1" />
    <el-dialog v-model="showProfileDialog" :center="true" width="750px" :show-close="false" :lock-scroll="true"
      class="profile-dialog" style="border-radius:20px">
      <section v-if="showProfileDialog" class="profile-dialog__content">
        <div class="profile-dialog__header">
          <div class="title-text ml-4"><svg class="profile-dialog__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
              <path d="M8.8 11.7 11 13.9l4.5-4.5" />
            </svg>{{ editingProfileId === null ? "Nueva plantilla" : "Editar plantilla" }}</div><img
            class="profile-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt="Cerrar"
            @click="showProfileDialog = false" />
        </div>
        <div class="row d-flex profile-dialog__body">
          <div class="col-12 row d-flex mb-3">
            <div class="col-md-6 el-col-xs-24">
              <div class="form-item"><label>Nombre del perfil</label><el-input v-model="profileForm.name"
                  class="w-100" /></div>
            </div>
            <div class="col-md-6 el-col-xs-24">
              <div class="form-item"><label>Tipo empleado</label><el-select v-model="profileForm.appliesToEmployeeType"
                  class="w-100" placeholder="Todos" clearable><el-option v-for="opt in employeeTypeOptions"
                    :key="opt.value" :label="opt.label" :value="opt.value" /></el-select></div>
            </div>
            <div class="col-12">
              <h3>Permisos del perfil</h3>
              <EmployeePermissionMatrix :catalog="catalog" :permissions="permissions" @toggle="toggle" />
            </div>
          </div>
        </div>
        <p class="security-notice"><font-awesome-icon icon="exclamation-circle" /> Este perfil tiene permisos sensibles.
          Verifica cuidadosamente los cambios.</p>
        <div class="profile-dialog__actions"><button class="btn btn_yellow"
            @click="showProfileDialog = false">CANCELAR</button><button class="btn btn_red" :disabled="saving"
            @click="saveProfile">GUARDAR CAMBIOS</button></div>
      </section>
    </el-dialog>
  </section>
</template>

<style lang="scss" scoped>
.permission-profiles {
  color: #30385d
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 12px
}

.security-subtitle__icon {
  fill: none;
  height: 27px;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 27px
}

.security-subtitle .title-text {
  color: #474ce6;
  font-size: 2.5em;
  margin-left: 1rem
}

.permission-profiles__new {
  height: 40px;
  margin-left: auto;
  min-height: 40px;
  padding: 0 18px;
  width: auto
}

.profiles-table table {
  min-width: 720px
}

.profiles-table th {
  color: #474ce6;
  text-transform: uppercase
}

.profiles-table td {
  vertical-align: middle
}

.row-actions {
  display: flex;
  gap: 8px;
  justify-content: center
}

.row-actions :deep(.fa-layers) {
  cursor: pointer
}

.table-mobile {
  display: none
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 16px 0 22px
}

.empty {
  color: #7c83a2;
  text-align: center
}

.profile-dialog :deep(.el-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px)
}

.profile-dialog :deep(.el-dialog__body) {
  padding: 0
}

.profile-dialog__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 2px
}

.profile-dialog__header .title-text {
  align-items: center;
  color: #474ce6;
  display: flex;
  font-size: 2.5em;
  font-weight: 900;
  letter-spacing: -.05em
}

.profile-dialog__icon {
  fill: none;
  flex: 0 0 40px;
  height: 40px;
  margin-right: 12px;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 40px
}

.profile-dialog__close {
  height: 24px;
  object-fit: contain;
  width: 24px
}

.profile-dialog__body {
  margin: 0
}

.profile-dialog__content h3 {
  border-bottom: 1px solid #e3e5f0;
  color: #474ce6;
  font-size: .8rem;
  margin: 18px 0 8px;
  padding-bottom: 8px;
  text-transform: uppercase
}

.security-notice {
  background: #fff5d8;
  border: 1px solid #f7d20d;
  border-radius: 8px;
  color: #715e22;
  font-size: .78rem;
  margin: 12px 0;
  padding: 8px 10px
}

.security-notice .svg-inline--fa {
  color: #f7c800;
  margin-right: 5px
}

.profile-dialog__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 16px
}

.profile-dialog__actions .btn {
  width: 40%
}

@media(max-width:767px) {
  .table-not-mobile {
    display: none
  }

  .table-mobile {
    display: grid;
    gap: 12px
  }

  .security-subtitle {
    flex-wrap: wrap
  }

  .security-subtitle .title-text {
    font-size: 2em
  }

  .permission-profiles__new {
    margin-left: 0;
    margin-top: 10px;
    width: 100%
  }

  .profiles-mobile :deep(.el-card__body) {
    padding: 14px
  }

  .profiles-mobile p {
    display: grid;
    gap: 7px;
    grid-template-columns: 130px minmax(0, 1fr);
    margin: 9px 0 0;
    word-break: break-word
  }

  .profiles-mobile p b {
    color: #474ce6
  }

  .profile-card {
    cursor: pointer;
    position: relative
  }

  .profile-card__delete {
    background: transparent;
    border: 0;
    color: #f9151c;
    font-size: 1.7rem;
    position: absolute;
    right: 7px;
    top: 5px
  }

  .profile-dialog__header {
    align-items: flex-start;
    gap: 12px
  }

  .profile-dialog__header .title-text {
    font-size: 26px;
    line-height: 1.15;
    margin-left: 0 !important
  }

  .profile-dialog__actions {
    flex-direction: column
  }

  .profile-dialog__actions .btn {
    width: 100%
  }
}
</style>
